import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { ButtonComponent, ComponentRow, SelectComponent } from '@spawnwire/shared';
import { Button, clsx } from '../../components/ui';
import { clickComponent } from './componentActions';

const BUTTON_CLASS: Record<Exclude<ButtonComponent['style'], 'link'>, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'bg-active text-fg hover:bg-line-strong',
  success: 'bg-green-600 text-white hover:brightness-110',
  danger: 'bg-danger text-white hover:brightness-110',
};

const base = 'inline-flex h-8 max-w-full items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';

function BotButton({ b, channelId, messageId }: { b: ButtonComponent; channelId: string; messageId: string }) {
  const [busy, setBusy] = useState(false);
  if (b.style === 'link') {
    return (
      <a href={b.url} target="_blank" rel="noopener noreferrer nofollow ugc" className={clsx(base, BUTTON_CLASS.secondary)}>
        <span className="truncate">{b.label}</span>
        <ExternalLink className="size-3.5 shrink-0" aria-hidden />
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled={b.disabled || busy}
      className={clsx(base, BUTTON_CLASS[b.style])}
      onClick={() => {
        setBusy(true);
        void clickComponent(channelId, messageId, b.customId!).finally(() => setBusy(false));
      }}
    >
      <span className="truncate">{b.label}</span>
    </button>
  );
}

function BotSelect({ s, channelId, messageId }: { s: SelectComponent; channelId: string; messageId: string }) {
  const initial = s.options.filter((o) => o.default).map((o) => o.value);
  const [picked, setPicked] = useState<string[]>(initial);
  const [busy, setBusy] = useState(false);
  const send = (values: string[]) => {
    setBusy(true);
    void clickComponent(channelId, messageId, s.customId!, values).finally(() => setBusy(false));
  };
  const field = 'h-9 w-full max-w-sm rounded-md border border-line bg-input px-2 text-sm text-fg outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-50';

  // One choice: picking it sends it. The menu goes back to its starting point, as the bot may change the message.
  if (s.maxValues === 1) {
    return (
      <select
        aria-label={s.placeholder ?? s.customId}
        className={field}
        disabled={s.disabled || busy}
        value={picked[0] ?? ''}
        onChange={(e) => {
          send([e.target.value]);
          setPicked(initial);
        }}
      >
        <option value="" disabled>
          {s.placeholder ?? 'Choose…'}
        </option>
        {s.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  // Several choices: tick them, then confirm.
  const enough = picked.length >= s.minValues && picked.length <= s.maxValues;
  return (
    <fieldset className="w-full max-w-sm rounded-md border border-line bg-input p-2" disabled={s.disabled || busy}>
      <legend className="px-1 text-xs text-muted">{s.placeholder ?? 'Choose'}</legend>
      <div className="flex flex-col gap-1">
        {s.options.map((o) => {
          const on = picked.includes(o.value);
          return (
            <label key={o.value} className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm hover:bg-hover">
              <input
                type="checkbox"
                className="mt-1"
                checked={on}
                disabled={!on && picked.length >= s.maxValues}
                onChange={() => setPicked((p) => (on ? p.filter((v) => v !== o.value) : [...p, o.value]))}
              />
              <span>
                {o.label}
                {o.description && <span className="block text-xs text-muted">{o.description}</span>}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs text-muted">
          {s.minValues === s.maxValues ? `Choose ${s.maxValues}` : `Choose ${s.minValues} to ${s.maxValues}`}
        </span>
        <Button size="sm" disabled={!enough} onClick={() => send(picked)}>
          Confirm
        </Button>
      </div>
    </fieldset>
  );
}

/** The buttons and menus a bot attached to a message. What they do is decided by the bot; the server checks every click. */
export function MessageComponents({ rows, channelId, messageId }: { rows: ComponentRow[]; channelId: string; messageId: string }) {
  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid="message-components">
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          {row.components.map((c, n) =>
            c.type === 'button' ? <BotButton key={c.customId ?? `${i}-${n}`} b={c} channelId={channelId} messageId={messageId} /> : <BotSelect key={c.customId} s={c} channelId={channelId} messageId={messageId} />,
          )}
        </div>
      ))}
    </div>
  );
}
